import React, { useState, useEffect } from 'react';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
// import { NewsletterModal } from '../NewsletterModal';
import { ViewState, User } from '@/src/types';
import { Capacitor } from '@capacitor/core';

interface AppLayoutProps {
  children: React.ReactNode;
  currentView: ViewState;
  onNavigate: (view: ViewState) => void;
  user: User | null;
  onLogout: () => void;
  bookmarkedIds: Set<string>;
}

const NewsletterModal = React.lazy(() => import('../NewsletterModal').then(module => ({ default: module.NewsletterModal })));

export function AppLayout({ children, currentView, onNavigate, user, onLogout, bookmarkedIds }: AppLayoutProps) {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isNewsletterOpen, setIsNewsletterOpen] = useState(false);
  const [mode, setMode] = useState<'client' | 'advocate'>('client');
  const isAndroid = Capacitor.getPlatform() === 'android';



  const handleCloseNewsletter = () => {
    setIsNewsletterOpen(false);
    // Suppress auto-pop-up again in the current tab session so we respect user intent
    sessionStorage.setItem('paklegal_newsletter_dismissed', 'true');
  };

  const handleOpenNewsletter = () => {
    setIsNewsletterOpen(true);
  };

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden font-sans">
      {/* Desktop Sidebar (hidden on mobile naturally via Tailwind, no isAndroid check needed) */}
      <div className="hidden lg:block h-full shrink-0 z-20">
        <Sidebar 
          currentView={currentView} 
          onNavigate={onNavigate} 
          isCollapsed={isSidebarCollapsed}
          toggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          user={user}
          onOpenNewsletter={handleOpenNewsletter}
        />
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar 
          user={user} 
          toggleSidebar={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)} 
          onLogout={onLogout} 
          onNavigate={onNavigate} 
          bookmarkedIds={bookmarkedIds} 
          isAndroid={isAndroid}
          currentView={currentView}
        />
        
        <main className={`flex-1 overflow-y-auto p-6 lg:p-8`}>
          <div className="h-full">
            {children}
          </div>
        </main>
      </div>
      
      {/* Mobile Sidebar Overlay (active on Android and small screens) */}
      {isMobileSidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setIsMobileSidebarOpen(false)} />
          <div className="relative z-50 h-full w-64 bg-slate-900 shadow-xl">
             <Sidebar 
                currentView={currentView} 
                onNavigate={(v) => { onNavigate(v); setIsMobileSidebarOpen(false); }} 
                isCollapsed={false}
                toggleCollapse={() => {}}
                user={user}
                onOpenNewsletter={handleOpenNewsletter}
              />
          </div>
        </div>
      )}

      {/* Newsletter Subscription Modal */}
      {isNewsletterOpen && (
        <React.Suspense fallback={null}>
          <NewsletterModal 
            isOpen={isNewsletterOpen} 
            onClose={handleCloseNewsletter} 
            currentUser={user} 
          />
        </React.Suspense>
      )}
    </div>
  );
}
